import {
  createLocalStorageKey,
  useLocalStorageKey,
} from 'hooks/useLocalStorageKey'
import {
  CART_UPSELL_IDS,
  type CartUpsellGenerationFields,
  type CartUpsellId,
  type CartUpsellOffer,
  type CartUpsellRecord,
  type CartUpsellRecords,
  CartUpsellRecordsSchema,
} from './cartUpsells.schemas'

const cartUpsellsStorageKey = createLocalStorageKey(
  'cart-upsells',
  CartUpsellRecordsSchema
)
const NO_CART_UPSELL_RECORDS: CartUpsellRecords = {}

export function useCartUpsellRecords(): CartUpsellRecords {
  return useLocalStorageKey(cartUpsellsStorageKey) ?? NO_CART_UPSELL_RECORDS
}

export function getCartUpsellOffer(
  record: CartUpsellRecords[CartUpsellId]
): CartUpsellOffer<CartUpsellId> | null {
  return record?.status === 'offered' ? record.offer : null
}

// TS can't match a generic id's offer or generation against CartUpsellRecord<Id>, so those shapes are spelled out.
function setCartUpsellRecord<Id extends CartUpsellId>(
  id: Id,
  record:
    | CartUpsellRecord<Id>
    | { status: 'offered'; offer: CartUpsellOffer<Id> }
    | (CartUpsellGenerationFields<Id> & {
        status: 'generating'
        jobId: string
        startedAt: number
      })
): void {
  cartUpsellsStorageKey.setValue({
    ...cartUpsellsStorageKey.getValue(),
    [id]: record,
  })
}

export function markUpsellGenerationRequested(id: CartUpsellId): void {
  setCartUpsellRecord(id, { status: 'pending', requestedAt: Date.now() })
}

// Requested before this page started loading and never given a jobId: that page's POST was cut off (a reload or a
// closed tab) or failed transiently.
export function isGenerationRequestInterrupted(
  record: CartUpsellRecord<CartUpsellId>
): boolean {
  return (
    record.status === 'pending' && record.requestedAt < performance.timeOrigin
  )
}

export function markUpsellGenerationStarted<Id extends CartUpsellId>(
  id: Id,
  generationFields: CartUpsellGenerationFields<Id>,
  jobId: string
): void {
  setCartUpsellRecord(id, {
    ...generationFields,
    status: 'generating',
    jobId,
    startedAt: Date.now(),
  })
}

export function markUpsellGenerated<Id extends CartUpsellId>(
  id: Id,
  offer: CartUpsellOffer<Id>
): void {
  setCartUpsellRecord(id, { status: 'offered', offer })
}

export function markUpsellGenerationFailed(
  id: CartUpsellId,
  errorCode: string
): void {
  setCartUpsellRecord(id, { status: 'failed', errorCode })
}

export function markUpsellApplied(id: CartUpsellId): void {
  setCartUpsellRecord(id, { status: 'applied' })
}

export function markUpsellDismissed(id: CartUpsellId): void {
  setCartUpsellRecord(id, { status: 'dismissed' })
}

export function clearCartUpsellRecords(): void {
  cartUpsellsStorageKey.removeValue()
}

export function getCartUpsellStatuses(): Partial<
  Record<CartUpsellId, CartUpsellRecord<CartUpsellId>['status']>
> {
  const records = cartUpsellsStorageKey.getValue() ?? NO_CART_UPSELL_RECORDS
  return Object.fromEntries(
    CART_UPSELL_IDS.flatMap((id) => {
      const record = records[id]
      return record ? [[id, record.status]] : []
    })
  )
}
